<script lang="ts" setup>
// -------------------------------
// 🔹 Imports Vue & composables
// -------------------------------
import { computed, onUnmounted, toRaw, watch, watchEffect } from 'vue';
import { useQuery } from '@tanstack/vue-query';

// -------------------------------
// 🔹 Composants
// -------------------------------
import BaseCheckBox from '@/components/common/BaseCheckBox.vue';
import BaseProduct from '@/components/common/BaseProduct.vue';
import BaseButtonPrimary from '@/components/common/BaseButtonPrimary.vue';
import NewProduct from '@/components/product/NewProduct.vue';

// -------------------------------
// 🔹 Stores Pinia
// -------------------------------
import { useCommerceStore } from '@/store/commerce.store';
import { useCategoryStore } from '@/store/category.store';
import { useProductStore } from '@/store/product.store';
import { useDialogStore } from '@/store/dialog.store';

// -------------------------------
// 🔹 Mutations
// -------------------------------
import { CategorieMutation } from '@/mutations/categorie.mutations';

// -------------------------------
// 🔹 Utils & assets
// -------------------------------
import { arrwo_inline, dots } from '@/assets/svg/arraw_pagination';
import { arrow_down_category } from '@/assets/svg/category';

// -------------------------------
// 🔹 Refs & données
// -------------------------------
import {
  slice_paginate_order,
  data_array_from_filter_order,
} from '@/refs/dashboard.refs';
import {
  designation_product_filter,
  category_product_filter,
  checkedValue,
  pageProduct,
  stepTwoActive,
  stepOneActive,
  stepThreeActive,
  stepFourActive,
  form_product_step_one,
  category_product,
  dynamicExtraOptions,
  previewUrl,
  isReadProduct,
  productId,
  NumberOfTotalField,
  isUpdatedProduct,
  productVariantsList,
} from '@/refs/product.refs';
import {
  idToDelete,
  isStepThreeActivated,
  isStepTwoActivated,
} from '@/refs/commerce.refs';
import {
  descriptionPageProduct,
  titlePageProduct,
  isNewProduct,
  searchQueryProduct,
} from '@/refs/product.refs';
import { isLoaderLoginFormActivated } from '@/refs/login.form.refs';
import BaseNotDataFound from '@/components/common/BaseNotDataFound.vue';
import { searchIcon } from '@/assets/svg/common.svg';
import { restaurantId } from '@/refs/common.refs';
import {
  isPromoProductStep,
  productIds_promotion,
} from '@/refs/promotion.refs';

// -------------------------------
// 🔹 Stores instanciés
// -------------------------------
const commerceStore = useCommerceStore();
const dialogueStore = useDialogStore();
const categoryStore = useCategoryStore();
const productStore = useProductStore();

// -------------------------------
// 🔹 Mutations instanciés et destruturations
// -------------------------------
const { handleCreateCategory } = CategorieMutation();

// -------------------------------
// 🔹 Récupération des produits
// -------------------------------
const {
  data: dataGetProduct,
  isLoading: isLoadingGetProduct,
  isError: isErrorGetProduct,
  refetch: refectGetProduct,
} = useQuery({
  queryKey: ['GetProduct'],
  queryFn: () => productStore.getProduct(),
});
const {
  data: dataGetCommerces,
  isLoading: isLoadingGetCommerces,
  isError: isErrorGetCommerces,
  refetch: refectGetCommerces,
} = useQuery({
  queryKey: ['GetCommerces'],
  queryFn: () => commerceStore.getCommerces(),
});

// -------------------------------
// 🔹 Récupération des catégories
// -------------------------------
const {
  data: dataGetCategories,
  isLoading: isLoadingGetCategories,
  isError: isErrorGetCategories,
  refetch: refectGetCategories,
} = useQuery({
  queryKey: ['GetCategories'],
  queryFn: () => categoryStore.getCategory(),
});

// -------------------------------
// 🔹 Pagination DRY
// -------------------------------
const totalPages = computed(() =>
  Math.ceil(
    Number(dataGetProduct?.value?.total) /
      Number(dataGetProduct?.value?.pageSize),
  ),
);

/**
 * Met à jour les pages visibles et l’index de pagination
 */
const updatePaginate = (startIndex: number) => {
  const totalPagess = Array.from(
    { length: Number(totalPages.value) || 1 },
    (_, i) => i + 1,
  );
  data_array_from_filter_order.value = totalPagess.slice(
    startIndex,
    startIndex + 3,
  );
  slice_paginate_order.value = startIndex;
};

const handlePaginate = (pageNumber: number) => {
  const startIndex = Math.max(pageNumber - 1, 0);
  pageProduct.value = pageNumber;
  updatePaginate(startIndex);
  refectGetProduct();
};

const handleNextPage = () => {
  const startIndex = pageProduct.value;
  updatePaginate(startIndex);
  pageProduct.value =
    startIndex < totalPages.value ? startIndex + 1 : totalPages.value;
  refectGetProduct();
};

const handlePreviousPage = () => {
  if (pageProduct.value <= 1) return;
  const startIndex = Math.max(Number(toRaw(pageProduct.value)) - 3, 0);
  pageProduct.value = startIndex + 1;
  updatePaginate(startIndex);
  refectGetProduct();
};

const handleNewProduct = () => {
  isUpdatedProduct.value = false;
  isNewProduct.value = true;
  isReadProduct.value = false;
  titlePageProduct.value = 'Nouveau produit';
  descriptionPageProduct.value =
    'Produit / Nouveau produit / Informations du produit';

  form_product_step_one.designation = '';
  form_product_step_one.price = '';
  form_product_step_one.taxe = '';
  form_product_step_one.image = null;
  form_product_step_one.categorie = '';
  form_product_step_one.description = '';
  form_product_step_one.nameExtra = '';
  form_product_step_one.typeQty = '';
  form_product_step_one.valueQty = '';
  form_product_step_one.conditionExtra = '';
  form_product_step_one.taxe_teakeaway = '';
  form_product_step_one.taxte_onsite = '';
  form_product_step_one.SelectRestaurant = '';
  form_product_step_one.typeExtra = '';
  NumberOfTotalField.value = 6;
  category_product.value.length = 0;
  previewUrl.value = null;
  dynamicExtraOptions.value = [
    {
      designation: '',
      price: '',
      taxe: '',
      image: '',
      categorie: '',
      description: '',
      nameExtra: '',
      typeQty: '',
      valueQty: null,
      conditionExtra: '',
      required: false,
      extraOptions: [
        {
          name: '',
          price: '',
        },
      ],
    },
  ];

  isNewProduct.value = true;
  stepOneActive.value = true;
};
const handleCloseNewProduct = () => {
  if (
    descriptionPageProduct.value ===
    'Produit / Nouveau produit / Informations du produit / Ajouter des accompagnements'
  ) {
    stepOneActive.value = true;
    stepTwoActive.value = false;
    isStepThreeActivated.value = false;
    descriptionPageProduct.value =
      'Produit / Nouveau produit / Informations du produit';
  } else if (
    descriptionPageProduct.value ===
    'Produit / Nouveau produit / Informations du produit / Ajouter des accompagnements/Pévisualisation'
  ) {
    stepFourActive.value = false;
    stepOneActive.value = false;
    stepTwoActive.value = false;
    stepThreeActive.value = true;
    descriptionPageProduct.value =
      'Produit / Nouveau produit / Informations du produit / Ajouter des accompagnements/Personaliser le taxe et restaurant';
  } else if (
    descriptionPageProduct.value ===
    'Produit / Nouveau produit / Informations du produit / Ajouter des accompagnements/Personaliser le taxe et restaurant'
  ) {
    stepThreeActive.value = false;
    stepFourActive.value = false;
    stepOneActive.value = false;
    stepTwoActive.value = true;

    descriptionPageProduct.value =
      'Produit / Nouveau produit / Informations du produit / Ajouter des accompagnements';
  } else {
    stepFourActive.value = false;
    stepTwoActive.value = false;
    stepThreeActive.value = false;
    stepOneActive.value = false;
    titlePageProduct.value = 'Tous les produits';
    descriptionPageProduct.value = 'Produit / Liste des produits';

    isNewProduct.value = false;
  }
};

// -------------------------------
// 🔹 Dialogues
// -------------------------------
const openDialogue = (data: any) => {};

const openDialogueForDelete = (data: any) => {
  idToDelete.value = String(toRaw(data)?.id);
  dialogueStore.openDialog({
    isDeleted: true,
  });
};

// -------------------------------
// 🔹 Gestion des filtres checkbox
// -------------------------------
/**
 * Met à jour le filtre par désignation pour les produits
 */
const HandleCheckedDesignation = (designation: string, event: Event) => {
  // const target = event.target as HTMLInputElement;
  // if (target.checked) {
  //   designation_product_filter.value.push(designation);
  // } else {
  //   designation_product_filter.value = designation_product_filter.value.filter(
  //     (item) => item !== designation
  //   );
  // }
  refectGetProduct();
  console.log('Filtre désignation actuel :', designation_product_filter.value);
};

/**
 * Met à jour le filtre par catégorie pour les produits
 */
const HandleCheckedCategorie = (event: Event) => {
  pageProduct.value = 1;
  refectGetProduct();
  console.log('Filtre catégorie actuel :', isLoaderLoginFormActivated.value);
};
const handleClick = async (item: any) => {
  if (isPromoProductStep.value) return;
  try {
    designation_product_filter.value.length = 0;
    const response = (await productStore.getProductById(item?.id)) as any;
    productId.value = item?.id;
    form_product_step_one.designation = response?.data?.designation;
    form_product_step_one.description = response?.data?.description;
    form_product_step_one.price = response?.data?.price;
    form_product_step_one.SelectRestaurant = response?.data?.restaurant?.id;
    previewUrl.value = String(response?.data?.image?.info?.secureUrl);

    category_product.value = response?.data?.categories.map((it: any) => {
      return it?.category?.id;
    });

    productVariantsList.value = (response?.data?.variants ?? []).map(
      (v: any) => ({
        id: v.id,
        label: v.label,
        price: v.price,
        isDefault: v.isDefault,
        status: v.status,
      }),
    );

    const isEmpty = response?.data?.extraMenus?.length === 0;

    if (isEmpty) {
    }
    dynamicExtraOptions.value = response?.data?.extraMenus.map((i: any) => {
      return {
        designation: '',
        price: '',
        taxe: '',
        categorie: '',
        description: '',
        image: '',
        nameExtra: i?.title,
        typeQty: '',
        conditionExtra: i?.choiceType,
        required: i?.required,
        extraOptions: i?.options.map((opt: any) => {
          return {
            name: opt?.content,
            price: opt?.price,
          };
        }),
      };
    });

    descriptionPageProduct.value = 'Produit / Voir tous les produits';
    titlePageProduct.value = 'Détail du  produit';
    stepOneActive.value = false;
    stepTwoActive.value = false;
    stepThreeActive.value = false;
    stepFourActive.value = true;
    isNewProduct.value = true;
    isReadProduct.value = true;
  } catch (error) {}
};

// -------------------------------
// 🔹 Debugging (optionnel)
// -------------------------------

const handleSeeAllProduct = () => {
  isNewProduct.value = false;
  stepFourActive.value = false;
  stepTwoActive.value = false;
  stepThreeActive.value = false;
  stepOneActive.value = false;
  isUpdatedProduct.value = false;
  titlePageProduct.value = 'Tous les produits';
  descriptionPageProduct.value = 'Produit / Liste des produits';
};

const watchedValues = [
  isNewProduct,
  isUpdatedProduct,
  isStepThreeActivated,
  isStepTwoActivated,
  stepOneActive,
  stepFourActive,
];

watch(
  watchedValues.map((v) => () => v.value),
  () => {
    refectGetCategories();
    refectGetProduct();
  },
);

onUnmounted(() => {
  // Empêche les updates async après démontage
});
</script>

<template>
  <header class="flex w-full flex-row items-center justify-between gap-4">
    <div class="space-y-2" v-show="!isPromoProductStep">
      <h1
        class="text-25px text-grand-text font-tt-semiBold-Normal leading-[35px]"
      >
        {{ titlePageProduct }} /({{ dataGetProduct?.total }})
      </h1>
      <p
        v-show="!isUpdatedProduct"
        @click="handleCloseNewProduct"
        class="text-16px hover:text-selection text-secondary-text font-tt-normal-Normal cursor-pointer leading-normal hover:underline"
      >
        {{ descriptionPageProduct }}
      </p>
    </div>

    <div class="flex w-full flex-1 flex-row gap-3" v-if="!isNewProduct">
      <div class="flex flex-1 flex-col gap-2">
        <div
          class="relative mx-auto flex w-full max-w-md flex-1 flex-col gap-2"
        >
          <div
            class="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 transform"
            v-html="searchIcon"
          ></div>

          <input
            type="text"
            v-model="searchQueryProduct"
            placeholder="Rechercher par desigantion"
            class="border-border-input focus:ring-primary-900 focus:border-primary-900 hover:border-border-input w-full rounded-xl border bg-white py-2 pr-3 pl-10 text-gray-700 placeholder-gray-400 shadow-sm transition-all focus:ring-2 focus:outline-none"
          />
        </div>
      </div>
      <div class="flex w-full flex-1 flex-col gap-2">
        <select
          id="commerce"
          name="commerce_associé"
          v-model="restaurantId"
          required
          class="border-border-input focus:ring-primary-900 focus:border-primary-900 w-full rounded-xl border bg-white px-3 py-2 text-sm text-gray-700 placeholder-gray-400 shadow-sm transition-all outline-none hover:border-gray-400 focus:ring-2"
        >
          <option value="" disabled>Veuillez sélectionner un commerce</option>
          <option
            v-for="value in dataGetCommerces?.data"
            :value="value?.id"
            class="text-14px text-secondary-text"
          >
            {{ value.designation }}
          </option>
        </select>
      </div>
    </div>

    <div
      v-show="!isNewProduct && !isPromoProductStep"
      @click="handleNewProduct"
    >
      <BaseButtonPrimary
        :isClicked="isLoaderLoginFormActivated"
        type="submit"
        label="Créer un nouveau produit"
        style_of_btn="primary_color_of_btn "
        class="text-white"
      />
    </div>
    <div
      v-show="isUpdatedProduct && !isPromoProductStep"
      @click="handleSeeAllProduct"
    >
      <BaseButtonPrimary
        :isClicked="isLoaderLoginFormActivated"
        type="submit"
        label="Voir tous  les produits disponible"
        style_of_btn="primary_color_of_btn "
        class="text-white"
      />
    </div>
  </header>

  <div class="h-8" />
  <section class="space-y-[16px] rounded-2xl bg-white p-4" v-if="!isNewProduct">
    <div class="flex flex-row">
      <div class="flex w-[70%] flex-row flex-wrap gap-2">
        <div
          v-for="item in dataGetProduct?.data.filter((it) =>
            it.designation.includes(searchQueryProduct),
          )"
          v-if="dataGetProduct?.data.length !== 0"
        >
          <div class="relative flex flex-row items-center">
            <div v-show="isPromoProductStep" class="absolute top-0">
              <BaseCheckBox
                required="true"
                name="checkbox"
                type="checkbox"
                v-model="productIds_promotion"
                :value="item?.id"
                :id="item?.id"
              />
            </div>

            <BaseProduct
              @handleClick="handleClick(item)"
              :promotionTitle="'Personnalisable'"
              :promotionValue="0 + '%'"
              :promotionDesc="'Offert'"
              :price="item?.price"
              :categorie="item?.categories?.[0]?.category?.designation"
              :designation="item?.designation"
              :descritpion="item?.description"
              :img="item?.image?.info?.url"
              :alt="item?.image?.designation"
            />
          </div>
        </div>

        <div v-else>
          <BaseNotDataFound />
        </div>
      </div>

      <div class="w-[30%] space-y-[16px]">
        <div class="flex flex-row items-center justify-between">
          <p
            class="text-18px font-tt-medium-Normal text-grand-text leading-normal"
          >
            Filtre
          </p>
        </div>

        <div class="w-full">
          <div class="flex w-full flex-row items-center justify-between">
            <p
              class="text-14px font-tt-medium-Normal text-grand-text leading-normal"
            >
              Catégpries
            </p>

            <div v-html="arrow_down_category" />
          </div>

          <div
            class="flex flex-row flex-wrap items-center justify-between gap-2"
          >
            <div
              class="my-4 flex flex-row items-center gap-2"
              v-for="item in dataGetCategories?.data"
            >
              <BaseCheckBox
                v-model="category_product_filter"
                @change="HandleCheckedCategorie"
                :id="item?.id"
                required="true"
                :name="item?.id"
                type="checkbox"
                :value="item.id"
              />
              <label
                :for="item?.id"
                class="text-12px font-tt-normal-Normal text-secondary-text leading-normal"
              >
                {{ item.designation }}
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <section class="flex flex-row items-center justify-between">
      <section class="flex flex-row items-center gap-2">
        <p
          class="text-14px text-secondary-text font-tt-normal-Normal leading-5"
        >
          Résultats par page
        </p>

        <p
          class="text-14px text-secondary-text font-tt-normal-Normal text-center leading-5 tabular-nums"
        >
          {{ dataGetProduct?.pageSize }}
        </p>
      </section>

      <section
        class="flex flex-row items-center gap-2"
        v-show="Number(totalPages - 1) !== 0"
      >
        <div
          v-html="arrwo_inline"
          @click="handlePreviousPage"
          :class="Number(pageProduct) === 1 ? 'opacity-[0.4]' : 'opacity-100'"
          class="flex rotate-180 transform cursor-pointer items-center justify-center"
        />

        <div
          class="flex flex-row items-center gap-[16px]"
          v-show="Number(totalPages - 1) !== 0"
        >
          <p
            @click="handlePaginate(number)"
            class="text-12px font-tt-normal-Normal cursor-pointer"
            v-for="(number, index) in data_array_from_filter_order"
            :key="index"
            :class="
              Number(pageProduct) === number
                ? 'text-selection bg-fond-beige'
                : 'text-grand-text bg-white'
            "
          >
            {{ number }}
          </p>
        </div>

        <div
          v-show="Number(totalPages - 1) !== 0"
          @click="handleNextPage"
          v-html="dots"
          class="flex cursor-pointer items-center justify-center p-[13px]"
        />

        <div
          class="text-12px text-grand-text font-tt-normal-Normal cursor-pointer"
          @click="handlePaginate(totalPages - 1)"
        >
          {{ totalPages - 1 === 0 ? '1' : totalPages }}
        </div>

        <div
          v-show="Number(totalPages - 1) !== 0"
          v-html="arrwo_inline"
          @click="handleNextPage"
          class="flex cursor-pointer items-center justify-center"
        />
      </section>
    </section>
  </section>

  <transition
    enter-active-class="transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]"
    enter-from-class="opacity-0 translate-y-4 scale-95"
    enter-to-class="opacity-100 translate-y-0 scale-100"
    leave-active-class="transition-all duration-400 ease-[cubic-bezier(0.4,0,0.2,1)]"
    leave-from-class="opacity-100 translate-y-0 scale-100"
    leave-to-class="opacity-0 translate-y-2 scale-95"
  >
    <section v-if="isNewProduct">
      <NewProduct />
    </section>
  </transition>
</template>
